import classNames from "classnames";
import filepicker from "filepicker-js";
import * as filestack from "filestack-js";
import allowedImageMimetypes from "helpers/allowedImageMimetypes";
import fileNameSanitizer from "helpers/sanitizers";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { arrayMove } from "react-sortable-hoc";
import ModalDeleteConfirmation from "components/ModalDeleteConfirmation";
import LoadingCurtain from "../LoadingCurtain";
import Placeholder from "../Placeholder";
import SortableImageList from "../SortableImageList";

import {
  FILEPICKER_API_KEY, FILESTACK_IMAGE_PATH, S3_URL, FILESTACK_BUCKET,
} from "../../helpers/constants";

import "./_style.scss";

const client = filestack.init(FILEPICKER_API_KEY);

class MultiImageInput extends Component {
  static propTypes = {
    alt: PropTypes.string,
    isInvalid: PropTypes.bool,
    imageWidth: PropTypes.number,
    imageHeight: PropTypes.number,
    cropRatio: PropTypes.number,
    onChange: PropTypes.func,
  };

  state = {
    images: [],
    imagePlaceholders: this.props.storedImgSrc ? 4 : 5,
    deleteImageModalOpen: false,
    deleteAssetId: null,
    deleteErrorMessage: "",
    isUploading: false,
    expectedUploadedCount: null,
    uploadErrorMessage: null,
  };

  UNSAFE_componentWillMount() {
    this.setImages();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.storedImgSrc !== prevProps.storedImgSrc) {
      this.setImages();
    }

    if (this.expectedUploadedCount && this.props.storedImgSrc.length >= this.expectedUploadedCount) {
      this.setState({
        isUploading: false,
      });
      this.expectedUploadedCount = null;
    }

    if (this.state.isUploading !== prevState.isUploading) {
      this.props.onUploadingChange?.(this.state.isUploading);
    }
  }

  setImages = () => {
    this.setState({
      images: this.props.storedImgSrc,
      imagePlaceholders: 5 - this.props.storedImgSrc.length,
    });
  };

  onSelectionError = (errorMessage) => {
    this.setState({
      uploadErrorMessage: errorMessage,
    });
  };

  onSuccess = async (Blob) => {
    const {
      onChange,
      onUpload,
    } = this.props;

    this.setState({
      isUploading: true,
      uploadErrorMessage: null,
    });

    const currentImagesLength = this.props.storedImgSrc?.length || 0;

    const successfulUploads = Blob.filesUploaded || [];
    const failedUploads = Blob.filesFailed || [];
    const hasUploadErrors = failedUploads.length > 0;

    const storeUrlPromise = (storeUrl, filename) => new Promise((resolve, reject) => {
      filepicker.storeUrl(
        storeUrl,
        {
          filename: fileNameSanitizer(filename),
          path: FILESTACK_IMAGE_PATH,
        },
        (s3Blob) => {
          if (s3Blob) {
            resolve(s3Blob);
          } else {
            reject(new Error("Failed to upload to S3"));
          }
        },
      );
    });

    const uploadedPromises = successfulUploads.map((image, index) => storeUrlPromise(image.url, image.filename)
      .then((s3Blob) => {
        const imgSrc = `${S3_URL}${s3Blob.key}`;
        const weight = currentImagesLength === 0
          ? (index === 0 ? 1 : index + 1)
          : (index === 0 ? currentImagesLength : currentImagesLength + index);

        onUpload(image, imgSrc, weight);

        if (typeof onChange === "function") {
          onChange(imgSrc);
        }

        return imgSrc;
      }));

    this.expectedUploadedCount = currentImagesLength + successfulUploads.length;
    await Promise.all(uploadedPromises);

    if (hasUploadErrors) {
      this.setState({
        uploadErrorMessage: "Some files failed to upload. Please retry failed files.",
      });
    }
  };

  openDeleteImageModal = (event) => {
    if (event.target.value) {
      this.setState({
        deleteImageModalOpen: !this.state.deleteImageModalOpen,
        deleteAssetId: event.target.value,
      });
    } else {
      this.setState({
        deleteErrorMessage: "Please reload the page to try deleting again.",
      });
    }
  };

  deleteImage = () => {
    const {
      deleteSelectedImage,
    } = this.props;

    const {
      images,
      deleteAssetId,
    } = this.state;

    this.setState({
      images: images.length > 1
        ? images.filter((i) => i.id != deleteAssetId)
        : (images.length === 1 ? images.splice() : []),
    });

    this.closeDeleteModal();

    deleteSelectedImage(deleteAssetId);
  };

  openPickerModal = () => {
    const cropRatio = this.props?.cropRatio;

    const picker = client.picker({
      fromSources: ["local_file_system"],
      maxFiles: 15,
      imageMax: cropRatio ? [2235, 1260] : [1240, 700],
      imageMin: [745, 420],
      accept: allowedImageMimetypes,
      storeTo: {
        location: "s3",
        container: FILESTACK_BUCKET,
        region: "ap-southeast-2",
      },
      transformations: {
        crop: {
          aspectRatio: cropRatio ?? 0,
          force: !!cropRatio,
        },
      },
      onUploadDone: this.onSuccess,
      onFileSelected: (file) => {
        if (file.size > 4.5 * 1024 * 1024) {
          // 4.5 MB limit
          this.onSelectionError("Each image must be less than 4.5Mb.");
          picker.close();
        }
        const safeName = file.filename.replace(/[^a-zA-Z0-9_.-]/g, "").trim();
        // Fallback to timestamp if name becomes empty - should have extension though
        const newName = safeName || `img_${Date.now()}`;
        return { ...file, filename: newName };
      },
    });

    picker.open();
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedImages = arrayMove(this.state.images, oldIndex, newIndex);

    this.setState({
      images: updatedImages,
    });

    this.props.updateImagesOrder(updatedImages);
  };

  closeDeleteModal() {
    this.setState({
      deleteImageModalOpen: !this.state.deleteImageModalOpen,
      deleteAssetId: null,
    });
  }

  render() {
    const {
      isInvalid,
    } = this.props;

    const { images, isUploading } = this.state;
    const filepickerClassNames = "filestack-image-button placeholder --drag-and-drop --image-input image-input__containers --primary --icon";

    return (
      <div>
        <LoadingCurtain isLoading={isUploading} loadingText="Uploading">
          <div
            className={
              classNames(
                "multi-image-input",
                {
                  "--isInvalid": isInvalid,
                },
              )
            }
          >
            {
              images.length >= 1
                ? (
                  <>
                    <SortableImageList
                      axis="xy"
                      items={images}
                      onSortEnd={this.onSortEnd}
                      onClick={this.openDeleteImageModal}
                      children={(
                        <button type="button" className={filepickerClassNames} onClick={this.openPickerModal}>
                          <p>Add images</p>
                        </button>
                      )}
                      placeholders={
                        this.state.imagePlaceholders > 0 && [...Array(this.state.imagePlaceholders)].map((i, index) => (
                          <Placeholder
                            look="drag-and-drop"
                            className="--image-input image-input__containers --default-icon"
                            isInvalid={isInvalid}
                            key={index}
                            data-test="image-input-placeholders"
                          />
                        ))
                      }
                    />

                    {this.state.uploadErrorMessage !== null && (
                      <p className="multi-image-input-error">{this.state.uploadErrorMessage}</p>
                    )}
                  </>
                )
                : (
                  <div className="multi-image-input --empty">
                    <button type="button" className={filepickerClassNames} onClick={this.openPickerModal}>
                      <p>Add images</p>
                    </button>
                    {
                      this.state.imagePlaceholders > 0 && [...Array(this.state.imagePlaceholders)].map((i, index) => (
                        <Placeholder
                          look="drag-and-drop"
                          className="--image-input image-input__containers --default-icon"
                          isInvalid={isInvalid}
                          key={index}
                          data-test="image-input-placeholders"
                        />
                      ))
                    }
                  </div>
                )
            }

            <ModalDeleteConfirmation
              key="modal-delete-confirm__image-input"
              onDelete={() => this.deleteImage()}
              onCancel={() => this.closeDeleteModal()}
              modelName="Image"
              isOpen={this.state.deleteImageModalOpen ?? false}
            />
          </div>

          {this.state.deleteErrorMessage !== "" && <span className="error-display__pop-up">{this.state.deleteErrorMessage}</span>}
        </LoadingCurtain>
      </div>
    );
  }
}

export default MultiImageInput;
